import React, { useEffect, useState } from 'react'
import { useStock } from '../../../context/StockProvider';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import OutletSearch from '../../SelectSearch/OutletSearch';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReqInputForm from './ReqInputForm';
import ReqListTable from './ReqListTable';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "34px",
        height: "34px",
        fontSize: "0.875rem",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: "#3b82f6" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "34px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 6px" }),
    menu: (base) => ({ ...base, zIndex: 50, fontSize: "0.875rem" }),
    option: (base, { isFocused }) => ({
        ...base,
        backgroundColor: isFocused ? "#EFF6FF" : "white",
        color: "#111827",
    }),
};

const readonlyClass = "col-span-2 w-full px-2 py-1 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";
const editableClass = "col-span-2 w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";

const INITIAL_ITEM = {
    ItemName: "",
    BrandName: "",
    Quantity: "",
    Rate: "",
    GroupName: "",
    UOM: "",
    CurrentLevel: ""
};

const CreateReq = () => {
    const { stockItems, stockLoading, stockError } = useStock();
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const itemOptions = stockItems.map((item) => ({
        label: item.ItemName,
        value: item.ItemName,
        GroupName: item.GroupName
    }));

    const today = new Date().toLocaleDateString('en-CA');

    const [selectedOutlet, setSelectedOutlet] = useState();
    const [issueDate, setIssueDate] = useState(today);
    const [costCenterOptions, setCostCenterOptions] = useState([]);
    const [loadingCC, setLoadingCC] = useState(false);
    const [errorCC, setErrorCC] = useState("");
    const [selectedCostCenter, setSelectedCostCenter] = useState(null);
    const [itemData, setItemData] = useState(INITIAL_ITEM);
    const [list, setList] = useState([]);
    const [error, setError] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);

    const fetchCostCenters = async () => {
        if (!selectedOutlet) return;

        try {
            setLoadingCC(true);
            setErrorCC("");

            const res = await axios.post(`${baseUrl}/get_costcenters_by_outlet`, {
                outlet_name: selectedOutlet,
                token
            });

            const formatted = (res.data?.data || []).map((cc) => ({
                label: cc.costcenter_name,
                value: cc.costcenter_name,
                id: cc.id,
            }));

            setCostCenterOptions(formatted);
        } catch (error) {
            console.error("Failed to fetch cost centers", error);
            toast.error("Failed to load cost centers");
            setErrorCC(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to load cost centers");
        } finally {
            setLoadingCC(false);
        }
    };

    useEffect(() => {
        if (token && selectedOutlet) {
            fetchCostCenters();
            setSelectedCostCenter(null);
            setList([]);
        }
    }, [token, selectedOutlet]);

    const handleItemChange = (option) => {
        setError("");

        if (!option) {
            setItemData(INITIAL_ITEM);
            return;
        }

        const item = stockItems.find(i => i.ItemName === option.value);

        if (!item) return;

        setItemData({
            ItemName: item.ItemName,
            BrandName: item.BrandName || "",
            Quantity: "",
            Rate: item.Rate || "",
            GroupName: item.GroupName || "",
            UOM: item.UOM,
            CurrentLevel: item.CurrentLevel
        });

        setError("");
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;

        setError("");

        if (value && parseFloat(value) > itemData.CurrentLevel) {
            setError(`Cannot exceed stock (${itemData.CurrentLevel})`);
        } else {
            setError("");
        }

        setItemData(prev => ({
            ...prev,
            Quantity: value
        }));
    };

    const handleAdd = () => {
        if (!itemData.ItemName) {
            return setError("Please select item");
        }

        if (!itemData.Quantity || parseFloat(itemData.Quantity) <= 0) {
            return setError("Enter valid quantity");
        }

        if (parseFloat(itemData.Quantity) > itemData.CurrentLevel) {
            return setError("Quantity exceeds stock");
        }

        const isDuplicate = list.some((item, index) =>
            item.ItemName === itemData.ItemName && index !== editingIndex
        );

        if (isDuplicate && editingIndex === null) {
            toast.warning(`${itemData.ItemName} already added`);
            return;
        }

        const newItem = {
            ...itemData,
            Quantity: parseFloat(itemData.Quantity)
        };

        if (editingIndex !== null) {
            const updatedList = [...list];
            updatedList[editingIndex] = newItem;
            setList(updatedList);
            setEditingIndex(null);
        } else {
            setList(prev => [...prev, newItem]);
        }

        setItemData(INITIAL_ITEM);
        setError("");
    };

    const handleEdit = (index) => {
        const item = list[index];
        setItemData({
            ...item,
            Quantity: item.Quantity.toString()
        });
        setEditingIndex(index);
        setError("");
    };

    const handleDelete = (index) => {
        const updatedList = list.filter((_, i) => i !== index);
        setList(updatedList);

        if (editingIndex === index) {
            setEditingIndex(null);
            setItemData(INITIAL_ITEM);
            setError("");
        }
        else if (editingIndex > index) {
            setEditingIndex(editingIndex - 1);
        }
    };

    const getLocalDateTime = (dateStr) => {
        return `${dateStr}T00:00:00`;
    };

    const handleSubmit = async () => {
        if (!selectedOutlet) {
            return toast.error("Please select outlet");
        }

        if (!selectedCostCenter?.value) {
            return toast.error("Please select cost center");
        }

        if (list.length === 0) {
            return toast.error("Add at least one item");
        }

        try {
            const payload = {
                Date: getLocalDateTime(issueDate),
                CostCenter: selectedCostCenter.value,
                OutletName: selectedOutlet,
                purchase_from: "web",
                purchase_id: null,
                ItemDetailsList: list.map(item => ({
                    ItemName: item.ItemName,
                    GroupName: item.GroupName || "",
                    BrandName: item.BrandName || "",
                    Amount: Number(item.Quantity),
                    UOM: item.UOM,
                    Rate: Number(item.Rate) || 0
                }))
            };

            await axios.post(`${baseUrl}/poststockTransfer_web`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            toast.success("Stock issued successfully");

            setList([]);
            setItemData(INITIAL_ITEM);
            setSelectedCostCenter(null);
            setEditingIndex(null);

        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to issue stock");
        }
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow py-3">
                <div className="max-w-7xl mx-auto">
                    <div className='flex justify-center items-end mb-3'>
                        <div className='flex-1 flex justify-start items-end'>
                            <h2 className="text-base font-medium text-gray-800">
                                Create Store Requisition &nbsp;&nbsp; ----
                            </h2>
                        </div>

                        <div className='flex-1 flex justify-center'>
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>

                        <div className='flex-1 flex justify-end'>
                        </div>
                    </div>

                    {stockError && (
                        <div className="mt-10 py-2 px-3 bg-red-100 border border-red-400 text-red-700 rounded-md max-w-sm  mx-auto text-center">
                            {stockError}
                        </div>
                    )}

                    {stockLoading && (
                        <div className="text-center pt-10">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                            <p className="mt-2 text-gray-600">Loading items...</p>
                        </div>
                    )}

                    {!stockLoading && !stockError && (
                        <div className="bg-white p-4 rounded-md shadow-lg">
                            <div className='flex gap-5'>
                                <ReqInputForm
                                    issueDate={issueDate}
                                    setIssueDate={setIssueDate}
                                    costCenterOptions={costCenterOptions}
                                    selectedCostCenter={selectedCostCenter}
                                    setSelectedCostCenter={setSelectedCostCenter}
                                    loadingCC={loadingCC}
                                    selectedOutlet={selectedOutlet}
                                    itemOptions={itemOptions}
                                    itemData={itemData}
                                    handleItemChange={handleItemChange}
                                    handleQuantityChange={handleQuantityChange}
                                    error={error}
                                    setError={setError}
                                    editingIndex={editingIndex}
                                    setEditingIndex={setEditingIndex}
                                    setItemData={setItemData}
                                    handleAdd={handleAdd}
                                    INITIAL_ITEM={INITIAL_ITEM}
                                    editableClass={editableClass}
                                    readonlyClass={readonlyClass}
                                    selectStyles={selectStyles}
                                    baseUrl={baseUrl}
                                    token={token}
                                    fetchCostCenters={fetchCostCenters}
                                    errorCC={errorCC}
                                />

                                <ReqListTable
                                    list={list}
                                    editingIndex={editingIndex}
                                    handleEdit={handleEdit}
                                    handleDelete={handleDelete}
                                />
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={list.length === 0}
                                    className={`
                                        px-3 py-2 text-sm font-semibold rounded-md flex items-center gap-1.5 transition
                                        ${list.length === 0
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            : "bg-gradient-to-r from-green-500 to-green-700 text-white hover:scale-[1.03] hover:shadow-lg active:scale-95"
                                        }
                                    `}
                                >
                                    Issue to {selectedCostCenter?.label || "Cost Center"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    )
}

export default CreateReq